<template>
  <i
    :class="iconClasses"
    :style="iconStyle"
    @click="handleClick"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
  >
    <!-- SVG图标 -->
    <svg
      v-if="isSvg"
      :width="size"
      :height="size"
      :viewBox="viewBox"
      fill="currentColor"
    >
      <use :href="`#icon-${name}`" />
    </svg>

    <!-- 字体图标或Unicode -->
    <span v-else-if="isUnicode" v-html="unicodeChar"></span>

    <!-- 图片图标 -->
    <img
      v-else-if="isImage"
      :src="name"
      :alt="alt"
      :width="size"
      :height="size"
    />

    <!-- 字体图标类名 -->
    <span v-else></span>
  </i>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

/**
 * 图标类型
 */
export type IconType = 'svg' | 'font' | 'unicode' | 'image'

/**
 * 图标尺寸
 */
export type IconSize = 'mini' | 'small' | 'medium' | 'large' | 'xl' | number | string

/**
 * 图标Props接口
 */
interface IconProps {
  /** 图标名称或路径 */
  name: string
  /** 图标类型 */
  type?: IconType
  /** 图标尺寸 */
  size?: IconSize
  /** 图标颜色 */
  color?: string
  /** 是否旋转 */
  spin?: boolean
  /** 旋转角度 */
  rotate?: number
  /** 是否可点击 */
  clickable?: boolean
  /** 图标前缀 */
  prefix?: string
  /** SVG viewBox */
  viewBox?: string
  /** 图片alt属性 */
  alt?: string
  /** 自定义类名 */
  customClass?: string
  /** 是否禁用 */
  disabled?: boolean
}

// Props定义
const props = withDefaults(defineProps<IconProps>(), {
  type: 'font',
  size: 'medium',
  spin: false,
  rotate: 0,
  clickable: false,
  prefix: 'icon',
  viewBox: '0 0 24 24',
  disabled: false
})

const emit = defineEmits<IconEmits>()

// Emits定义
interface IconEmits {
  click: [event: MouseEvent]
  mouseenter: [event: MouseEvent]
  mouseleave: [event: MouseEvent]
}

// 响应式状态
const isHovered = ref(false)

// 计算属性
const iconClasses = computed(() => {
  const classes = ['dh-icon']

  // 基础类名
  if (props.type === 'font') {
    classes.push(`${props.prefix}-${props.name}`)
  }

  // 尺寸类名
  if (typeof props.size === 'string' && !['mini', 'small', 'medium', 'large', 'xl'].includes(props.size)) {
    // 自定义尺寸不添加类名
  } else if (typeof props.size === 'string') {
    classes.push(`dh-icon--${props.size}`)
  }

  // 状态类名
  if (props.spin) classes.push('dh-icon--spin')
  if (props.clickable) classes.push('dh-icon--clickable')
  if (props.disabled) classes.push('dh-icon--disabled')
  if (isHovered.value && props.clickable) classes.push('dh-icon--hovered')
  if (props.customClass) classes.push(props.customClass)

  return classes
})

const iconStyle = computed(() => {
  const style: Record<string, string> = {}

  // 尺寸样式
  if (typeof props.size === 'number') {
    style.fontSize = `${props.size}px`
    style.width = `${props.size}px`
    style.height = `${props.size}px`
  } else if (typeof props.size === 'string' && !['mini', 'small', 'medium', 'large', 'xl'].includes(props.size)) {
    style.fontSize = props.size
    style.width = props.size
    style.height = props.size
  }

  // 颜色样式
  if (props.color) {
    style.color = props.color
  }

  // 旋转样式
  if (props.rotate && !props.spin) {
    style.transform = `rotate(${props.rotate}deg)`
  }

  return style
})

// 图标类型判断
const isSvg = computed(() => props.type === 'svg')
const isUnicode = computed(() => props.type === 'unicode')
const isImage = computed(() => {
  return props.type === 'image' || /\.(png|jpg|jpeg|gif|svg|webp)$/i.test(props.name)
})

// Unicode字符处理
const unicodeChar = computed(() => {
  if (props.type === 'unicode') {
    // 处理Unicode编码
    if (props.name.startsWith('\\u')) {
      return String.fromCharCode(parseInt(props.name.slice(2), 16))
    }
    if (props.name.startsWith('&#x')) {
      return String.fromCharCode(parseInt(props.name.slice(3, -1), 16))
    }
    if (props.name.startsWith('&#')) {
      return String.fromCharCode(parseInt(props.name.slice(2, -1)))
    }
    return props.name
  }
  return ''
})

// 事件处理
const handleClick = (event: MouseEvent) => {
  if (props.disabled) return
  if (props.clickable) {
    emit('click', event)
  }
}

const handleMouseEnter = (event: MouseEvent) => {
  if (props.disabled) return
  isHovered.value = true
  emit('mouseenter', event)
}

const handleMouseLeave = (event: MouseEvent) => {
  if (props.disabled) return
  isHovered.value = false
  emit('mouseleave', event)
}

// 预定义图标映射
const ICON_MAP = {
  // 基础图标
  'home': 'house',
  'user': 'user',
  'setting': 'gear',
  'search': 'magnifying-glass',
  'close': 'xmark',
  'check': 'check',
  'plus': 'plus',
  'minus': 'minus',
  'edit': 'pen-to-square',
  'delete': 'trash',
  'save': 'floppy-disk',
  'upload': 'upload',
  'download': 'download',
  'refresh': 'arrow-rotate-right',
  'loading': 'spinner',

  // 箭头图标
  'arrow-up': 'arrow-up',
  'arrow-down': 'arrow-down',
  'arrow-left': 'arrow-left',
  'arrow-right': 'arrow-right',
  'chevron-up': 'chevron-up',
  'chevron-down': 'chevron-down',
  'chevron-left': 'chevron-left',
  'chevron-right': 'chevron-right',

  // 状态图标
  'success': 'circle-check',
  'error': 'circle-xmark',
  'warning': 'triangle-exclamation',
  'info': 'circle-info',

  // 功能图标
  'menu': 'bars',
  'more': 'ellipsis',
  'filter': 'filter',
  'sort': 'sort',
  'copy': 'copy',
  'link': 'link',
  'eye': 'eye',
  'eye-slash': 'eye-slash',
  'lock': 'lock',
  'unlock': 'unlock',

  // 主题图标
  'sun': 'sun',
  'moon': 'moon',
  'palette': 'palette',

  // 语言图标
  'globe': 'globe',
  'flag-cn': 'flag',
  'flag-us': 'flag',
  'flag-jp': 'flag'
}

// 获取实际图标名称
const getActualIconName = (name: string): string => {
  return ICON_MAP[name as keyof typeof ICON_MAP] || name
}
</script>

<style lang="scss" scoped>
.dh-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  vertical-align: middle;
  color: currentColor;
  transition: all 0.2s ease;

  // 尺寸样式
  &--mini {
    font-size: 12px;
    width: 12px;
    height: 12px;
  }

  &--small {
    font-size: 14px;
    width: 14px;
    height: 14px;
  }

  &--medium {
    font-size: 16px;
    width: 16px;
    height: 16px;
  }

  &--large {
    font-size: 20px;
    width: 20px;
    height: 20px;
  }

  &--xl {
    font-size: 24px;
    width: 24px;
    height: 24px;
  }

  // 状态样式
  &--spin {
    animation: icon-spin 1s linear infinite;
  }

  &--clickable {
    cursor: pointer;

    &:hover {
      color: var(--color-primary);
      transform: scale(1.1);
    }

    &:active {
      transform: scale(0.95);
    }
  }

  &--disabled {
    opacity: 0.5;
    cursor: not-allowed;

    &:hover {
      color: currentColor;
      transform: none;
    }
  }

  &--hovered {
    color: var(--color-primary);
  }

  // SVG样式
  svg {
    display: block;
    width: 100%;
    height: 100%;
  }

  // 图片样式
  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  // Unicode样式
  span {
    display: block;
    width: 100%;
    height: 100%;
    text-align: center;
    line-height: 1;
  }
}

// 动画定义
@keyframes icon-spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

// 常用图标颜色
.dh-icon {
  &.text-primary {
    color: var(--color-primary);
  }

  &.text-success {
    color: var(--color-success);
  }

  &.text-warning {
    color: var(--color-warning);
  }

  &.text-danger {
    color: var(--color-danger);
  }

  &.text-info {
    color: var(--color-info);
  }

  &.text-secondary {
    color: var(--color-text-secondary);
  }

  &.text-muted {
    color: var(--color-text-tertiary);
  }
}

// 暗色主题适配
.dark {
  .dh-icon {
    &--clickable:hover {
      color: var(--color-primary-light);
    }

    &--hovered {
      color: var(--color-primary-light);
    }
  }
}

// 响应式设计
@media (max-width: 768px) {
  .dh-icon {
    &--clickable {
      &:hover {
        transform: none; // 移动端禁用hover缩放
      }

      &:active {
        transform: scale(0.9);
      }
    }
  }
}

// 打印样式
@media print {
  .dh-icon {
    &--spin {
      animation: none;
    }

    &--clickable {
      cursor: default;
    }
  }
}
</style>
